import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUI, type HomeTab } from '../store/ui'
import { useAccounts } from '../store/accounts'
import { useIsLandscape } from '../hooks/useViewport'
import { useScrollDirection } from '../hooks/useScrollDirection'
import { MobileSearchModal } from './MobileSearchModal'
import { MobileTopRibbon } from './MobileTopRibbon'
import { AccountsBody, SettingsBody } from './TopPanel'
import { ActivityFeed } from './ActivityFeed'
import { GalleryControlsBody, ProfileGalleryControlsBody } from './GalleryControls'
import { FeedViewControlsBody } from './FeedViewControls'
import { DeactivatedBanner } from './DeactivatedBanner'
import { ApiHealthBanner } from './ApiHealthBanner'

type SheetKind = 'accounts' | 'more' | 'controls' | 'activity' | 'settings'

/**
 * Phone layout. Portrait: expandable top ribbon — row 1 (44px: avatar,
 * search button that opens the full-screen MobileSearchModal, activity,
 * controls, settings, expand chevron) over rows 2-3 (home tabs + quick
 * route links, open-tabs strip) — plus a bottom tab bar that collapses
 * to a thin strip while scrolling down (unless pinned). Panels open as
 * bottom sheets. Landscape: icon rail on the left + optional contextual
 * controls pane on the right (the desktop RightPanel's hover-open
 * doesn't exist on touch, so controls get explicit toggles here).
 */
export function MobileShell({ children }: { children: React.ReactNode }) {
  const landscape = useIsLandscape()
  const navigate = useNavigate()
  const loc = useLocation()

  const homeTab = useUI((s) => s.homeTab)
  const setHomeTab = useUI((s) => s.setHomeTab)
  const setPanel = useUI((s) => s.setPanel)
  const activityUnseen = useUI((s) => s.activityUnseen)
  const ribbonExpanded = useUI((s) => s.mobileTopRibbonExpanded)
  const setRibbonExpanded = useUI((s) => s.setMobileTopRibbonExpanded)
  const ribbonPinned = useUI((s) => s.mobileRibbonPinned)

  const accounts = useAccounts((s) => s.accounts)
  const activeId = useAccounts((s) => s.activeId)
  const activeAcc = accounts.find((a) => a.id === activeId)

  const [sheet, setSheet] = useState<SheetKind | null>(null)
  const [controlsOpen, setControlsOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  // Auto-hide: the portrait content pane reports its scroll direction;
  // heading down (>24px) rolls ribbon rows 2-3 and the bottom nav away,
  // heading up brings them back. Pinning disables the whole mechanism.
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null)
  const scrollDir = useScrollDirection(scrollEl)
  const [autoHidden, setAutoHidden] = useState(false)
  useEffect(() => {
    if (ribbonPinned) {
      setAutoHidden(false)
      return
    }
    setAutoHidden(scrollDir === 'down')
  }, [scrollDir, ribbonPinned])

  const ribbonVisible = ribbonExpanded && !autoHidden
  const navCollapsed = autoHidden

  const onHome = loc.pathname === '/'

  function toggleSheet(k: SheetKind) {
    setSheet((cur) => (cur === k ? null : k))
  }

  function toggleTopRibbon() {
    if (ribbonVisible) {
      setRibbonExpanded(false)
    } else {
      setRibbonExpanded(true)
      setAutoHidden(false)
    }
  }

  function goHomeTab(tab: HomeTab) {
    setSheet(null)
    setHomeTab(tab)
    setPanel('content')
    if (loc.pathname !== '/') navigate('/')
  }

  function goPost() {
    setSheet(null)
    navigate('/posting')
  }

  function controlsBody() {
    if (loc.pathname.startsWith('/feed/')) return <FeedViewControlsBody />
    if (loc.pathname.startsWith('/profile/')) return <ProfileGalleryControlsBody />
    if (onHome && homeTab === 'activity') return <GalleryControlsBody />
    if (onHome && homeTab === 'feed') return <FeedViewControlsBody />
    if (onHome && homeTab === 'profile') return <ProfileGalleryControlsBody />
    return <SettingsBody />
  }

  const avatarButton = (
    <button
      type="button"
      onClick={() => toggleSheet('accounts')}
      className="grid h-full w-12 shrink-0 place-items-center"
      title="accounts"
      style={{ background: sheet === 'accounts' ? '#16294d' : '#102341' }}
    >
      {activeAcc?.avatar ? (
        <img
          src={activeAcc.avatar}
          alt=""
          className="h-7 w-7 rounded-md object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="text-signal text-base">◈</span>
      )}
    </button>
  )

  const activityButton = (vertical: boolean) => (
    <button
      type="button"
      onClick={() => toggleSheet('activity')}
      className={`relative grid shrink-0 place-items-center text-base ${
        vertical ? 'h-12 w-full' : 'h-full w-10'
      } ${sheet === 'activity' ? 'block-active' : 'text-ink-200 hover:bg-white/5'}`}
      title="activity"
    >
      📡
      {activityUnseen > 0 ? (
        <span className="absolute right-1 top-1 bg-rose-500 px-1 text-[8px] font-bold text-white">
          {activityUnseen > 9 ? '9+' : activityUnseen}
        </span>
      ) : null}
    </button>
  )

  const sheetTitle: Record<SheetKind, string> = {
    accounts: 'accounts',
    more: 'more',
    controls: 'view controls',
    activity: 'activity',
    settings: 'settings',
  }

  const sheetEl = sheet ? (
    <Sheet
      title={sheetTitle[sheet]}
      side={landscape ? 'right' : 'bottom'}
      onClose={() => setSheet(null)}
    >
      {sheet === 'accounts' ? (
        <AccountsBody />
      ) : sheet === 'settings' ? (
        <SettingsBody />
      ) : sheet === 'activity' ? (
        <ActivityFeed />
      ) : sheet === 'controls' ? (
        controlsBody()
      ) : (
        <MoreMenu
          onGo={(fn) => {
            setSheet(null)
            fn()
          }}
          activeHandle={activeAcc?.handle}
          goHomeTab={goHomeTab}
          navigate={navigate}
        />
      )}
    </Sheet>
  ) : null

  const searchModal = searchOpen ? (
    <MobileSearchModal onClose={() => setSearchOpen(false)} />
  ) : null

  if (landscape) {
    return (
      <div className="h-app flex w-screen overflow-hidden">
        {/* left icon rail */}
        <nav className="surface-bar pl-safe flex w-14 shrink-0 flex-col items-stretch border-r border-white/10">
          {avatarButton && <div className="h-12 shrink-0">{avatarButton}</div>}
          <RailButton
            icon="⌂"
            label="feed"
            active={onHome && homeTab === 'feed'}
            onTap={() => goHomeTab('feed')}
          />
          <RailButton
            icon="▦"
            label="gallery"
            active={onHome && homeTab === 'activity'}
            onTap={() => goHomeTab('activity')}
          />
          <RailButton
            icon="✚"
            label="post"
            active={loc.pathname === '/posting'}
            onTap={goPost}
          />
          <RailButton
            icon="⌕"
            label="search"
            active={searchOpen}
            onTap={() => setSearchOpen(true)}
          />
          <RailButton
            icon="⋯"
            label="more"
            active={sheet === 'more'}
            onTap={() => toggleSheet('more')}
          />
          <div className="flex-1" />
          {activityButton(true)}
          <RailButton
            icon="⛭"
            label="controls"
            active={controlsOpen}
            onTap={() => setControlsOpen((v) => !v)}
          />
          <RailButton
            icon="⚙"
            label="settings"
            active={sheet === 'settings'}
            onTap={() => toggleSheet('settings')}
          />
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          <DeactivatedBanner />
          <ApiHealthBanner />
          <div className="surface-app min-h-0 min-w-0 flex-1 overflow-y-auto">
            {children}
          </div>
        </div>

        {controlsOpen ? (
          <aside
            className="w-[280px] shrink-0 overflow-y-auto border-l border-white/10"
            style={{ background: 'linear-gradient(180deg, #102341, #0a1830 45%, #081323)' }}
          >
            {controlsBody()}
          </aside>
        ) : null}

        {sheetEl}
        {searchModal}
      </div>
    )
  }

  // portrait — route to the collapsed strip's single icon
  const activeNav =
    loc.pathname === '/posting'
      ? { icon: '✚', label: 'post' }
      : onHome && homeTab === 'activity'
        ? { icon: '▦', label: 'gallery' }
        : onHome
          ? { icon: '⌂', label: 'feed' }
          : { icon: '⋯', label: 'more' }

  return (
    <div className="h-app flex w-screen flex-col overflow-hidden">
      <div className="surface-bar pt-safe shrink-0">
        <div className="flex h-11 items-stretch">
          {avatarButton}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex min-w-0 flex-1 items-center gap-2 px-3 text-left"
            style={{ background: '#0a1426' }}
            title="search"
          >
            <span className="shrink-0 text-[11px] text-ink-500">⌕</span>
            <span className="truncate text-xs text-ink-500">search handles, posts…</span>
          </button>
          {activityButton(false)}
          <button
            type="button"
            onClick={() => toggleSheet('controls')}
            className={`grid h-full w-10 shrink-0 place-items-center text-base ${
              sheet === 'controls' ? 'block-active' : 'text-ink-200 hover:bg-white/5'
            }`}
            title="view controls"
          >
            ⛭
          </button>
          <button
            type="button"
            onClick={() => toggleSheet('settings')}
            className={`grid h-full w-10 shrink-0 place-items-center text-base ${
              sheet === 'settings' ? 'block-active' : 'text-ink-200 hover:bg-white/5'
            }`}
            title="settings"
          >
            ⚙
          </button>
          <button
            type="button"
            onClick={toggleTopRibbon}
            className="grid h-full w-8 shrink-0 place-items-center text-xs text-ink-300 hover:bg-white/5"
            title={ribbonVisible ? 'collapse ribbon' : 'expand ribbon'}
          >
            {ribbonVisible ? '▴' : '▾'}
          </button>
        </div>
        <MobileTopRibbon visible={ribbonVisible} />
      </div>
      <DeactivatedBanner />
      <ApiHealthBanner />

      <div
        ref={setScrollEl}
        className="surface-app min-h-0 min-w-0 flex-1 overflow-y-auto"
      >
        {children}
      </div>

      <nav className="surface-bar pb-safe shrink-0 border-t border-white/10">
        {navCollapsed ? (
          <button
            type="button"
            onClick={() => setAutoHidden(false)}
            className="flex h-7 w-full items-center justify-center gap-2 text-ink-200 hover:bg-white/5"
            title="expand navigation"
          >
            <span className="text-sm leading-none">{activeNav.icon}</span>
            <span className="text-[9px] font-semibold uppercase tracking-wide">
              {activeNav.label}
            </span>
            <span className="text-[10px]">▴</span>
          </button>
        ) : (
          <div className="flex h-14 items-stretch">
            <TabButton
              icon="⌂"
              label="feed"
              active={onHome && homeTab === 'feed' && !sheet}
              onTap={() => goHomeTab('feed')}
            />
            <TabButton
              icon="▦"
              label="gallery"
              active={onHome && homeTab === 'activity' && !sheet}
              onTap={() => goHomeTab('activity')}
            />
            <TabButton
              icon="✚"
              label="post"
              active={loc.pathname === '/posting' && !sheet}
              onTap={goPost}
            />
            <TabButton
              icon="◎"
              label="accounts"
              active={sheet === 'accounts'}
              onTap={() => toggleSheet('accounts')}
            />
            <TabButton
              icon="⋯"
              label="more"
              active={sheet === 'more'}
              onTap={() => toggleSheet('more')}
            />
          </div>
        )}
      </nav>

      {sheetEl}
      {searchModal}
    </div>
  )
}

function TabButton({
  icon, label, active, onTap,
}: {
  icon: string; label: string; active: boolean; onTap: () => void
}) {
  return (
    <button
      type="button"
      onClick={onTap}
      className={`flex flex-1 flex-col items-center justify-center gap-0.5 ${
        active ? 'block-active' : 'text-ink-300 hover:bg-white/5'
      }`}
    >
      <span className="text-lg leading-none">{icon}</span>
      <span className="text-[9px] font-semibold uppercase tracking-wide">{label}</span>
    </button>
  )
}

function RailButton({
  icon, label, active, onTap,
}: {
  icon: string; label: string; active: boolean; onTap: () => void
}) {
  return (
    <button
      type="button"
      onClick={onTap}
      title={label}
      className={`grid h-12 w-full shrink-0 place-items-center text-lg ${
        active ? 'block-active' : 'text-ink-300 hover:bg-white/5'
      }`}
    >
      {icon}
    </button>
  )
}

function Sheet({
  title, side, onClose, children,
}: {
  title: string
  side: 'bottom' | 'right'
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={
          side === 'bottom'
            ? 'pb-safe overlay-panel absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto'
            : 'overlay-panel absolute inset-y-0 right-0 w-[320px] max-w-[85vw] overflow-y-auto'
        }
      >
        <header
          className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 px-3 py-1.5"
          style={{ background: '#102341' }}
        >
          <span className="text-[11px] font-bold uppercase tracking-wide text-white">
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center text-lg text-ink-100 hover:bg-white/10"
            title="close"
          >
            ✕
          </button>
        </header>
        {children}
      </div>
    </div>
  )
}

function MoreMenu({
  onGo, activeHandle, goHomeTab, navigate,
}: {
  onGo: (fn: () => void) => void
  activeHandle?: string
  goHomeTab: (t: HomeTab) => void
  navigate: (to: string) => void
}) {
  const items: { label: string; hint?: string; go: () => void }[] = [
    { label: 'discovery', hint: 'suggested accounts & feeds', go: () => goHomeTab('discovery') },
    { label: 'saved feeds', hint: 'your pinned / saved feeds', go: () => goHomeTab('saved') },
    ...(activeHandle
      ? [{
          label: `profile — @${activeHandle}`,
          hint: 'your own gallery & posts',
          go: () => navigate(`/profile/${encodeURIComponent(activeHandle)}`),
        }]
      : []),
    { label: 'calendar', hint: 'month grid · schedule per day', go: () => navigate('/calendar') },
    { label: 'queue', hint: 'pending & sent actions', go: () => navigate('/schedule') },
    { label: 'AI campaigns', hint: 'vision-AI curation rules', go: () => navigate('/ai-campaigns') },
    { label: 'auto-follow', hint: 'recurring follow campaigns', go: () => navigate('/auto-follow') },
    { label: 'search tabs', hint: 'query cached accounts', go: () => navigate('/tabs-search') },
    { label: 'error log', hint: 'sync · image · api · scheduler errors', go: () => navigate('/failure-log') },
    { label: 'all settings', hint: 'accounts · cache · export · DMs', go: () => navigate('/settings') },
  ]
  return (
    <div className="flex flex-col gap-px bg-white/10">
      {items.map((it) => (
        <button
          key={it.label}
          type="button"
          onClick={() => onGo(it.go)}
          className="flex items-center gap-2 bg-[#070d1c] px-4 py-3 text-left hover:bg-white/5"
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm text-ink-100">{it.label}</div>
            {it.hint ? <div className="text-[10px] text-ink-400">{it.hint}</div> : null}
          </div>
          <span className="text-ink-300">→</span>
        </button>
      ))}
    </div>
  )
}
